import { useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { categories, type Category } from '@/mocks/categories';
import { videos, getVideosByCategory } from '@/mocks/videos';
import { siteInfo } from '@/mocks/site';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeCategory, setActiveCategory] = useState<string>(slug || categories[0].slug);

  const currentCategory: Category | undefined = useMemo(
    () => categories.find((c) => c.slug === activeCategory),
    [activeCategory],
  );

  const categoryVideos = useMemo(
    () => getVideosByCategory(activeCategory),
    [activeCategory],
  );

  const displayCategory = currentCategory || categories[0];
  const pageTitle = `${displayCategory.name} - 打屁股视频大全`;
  const pageDescription = displayCategory.description;

  return (
    <>
      <ScrollToTop />
      <header>
        <Navbar />
      </header>
      <main className="pt-16">
        <div className="bg-background-100/50 border-b border-background-200/70">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: displayCategory.name },
              ]}
            />
            <div className="pb-6">
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
                {displayCategory.name}
              </h1>
              <p className="text-foreground-600 text-sm mt-2 max-w-2xl">
                {displayCategory.description}
              </p>
            </div>
          </div>
        </div>

        <div className="border-b border-background-200/70 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center gap-2 overflow-x-auto py-3 flex-wrap">
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => setActiveCategory(cat.slug)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                    activeCategory === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  <i className={`${cat.icon} mr-1.5`}></i>
                  {cat.name}
                  <span className="ml-1 text-xs opacity-70">({cat.videoCount})</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        <section className="py-8 md:py-12">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            {categoryVideos.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
                {categoryVideos.map((video) => (
                  <Link
                    key={video.id}
                    to={`/article/${video.slug}`}
                    className="group bg-background-100 rounded-lg overflow-hidden cursor-pointer transition-colors hover:bg-background-200/70"
                  >
                    <div className="aspect-video overflow-hidden relative">
                      <img
                        src={video.thumbnailUrl}
                        alt={video.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        loading="lazy"
                      />
                      <div className="absolute bottom-2 right-2 bg-black/70 text-background-50 text-xs px-2 py-0.5 rounded font-medium">
                        {video.duration}
                      </div>
                    </div>
                    <div className="p-4">
                      <h2 className="font-heading text-sm font-bold text-foreground-900 line-clamp-2 mb-2 group-hover:text-primary-600 transition-colors">
                        {video.title}
                      </h2>
                      <p className="text-foreground-500 text-xs line-clamp-2 leading-relaxed mb-3">
                        {video.description}
                      </p>
                      <div className="flex items-center gap-3 text-xs text-foreground-500">
                        <span className="flex items-center gap-1">
                          <i className="ri-eye-line"></i>
                          {video.views >= 10000
                            ? `${(video.views / 10000).toFixed(1)}万`
                            : video.views}
                        </span>
                        <span>{video.date}</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                  <i className="ri-video-line text-2xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-500 text-sm">该分类暂无视频内容</p>
                <Link to="/" className="inline-block mt-4 text-primary-500 hover:text-primary-600 text-sm font-medium">
                  返回首页
                </Link>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: pageTitle,
            description: pageDescription,
            url: `${siteInfo.url}/category/${displayCategory.slug}`,
            inLanguage: 'zh-CN',
            isPartOf: {
              '@type': 'WebSite',
              name: siteInfo.name,
              url: siteInfo.url,
            },
          }),
        }}
      />
    </>
  );
}